import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { PARCOURS_IMAGE_URLS, getImageUrlAndCrop } from '../data/parcoursIllustrations'
import { HORIZONS_IMAGE_URLS } from '../data/horizonsIllustrations'
import {
  getParcoursImageOverrides,
  setParcoursImageEntry,
  getHorizonsImageOverrides,
  setHorizonsImageEntry,
} from '../services/parcoursImagesService'

/**
 * Fusionne les URLs par défaut avec les overrides Firestore.
 * Les entrées override sans URL exploitable ne remplacent pas le défaut (évite d’effacer les images du bundle).
 */
function mergeImageEntries(defaults, overrides) {
  const out = { ...defaults }
  for (const [key, entry] of Object.entries(overrides)) {
    const { url } = getImageUrlAndCrop(entry)
    if (typeof url === 'string' && url.trim().length > 0) {
      out[key] = entry
    }
  }
  return out
}

const ParcoursImagesContext = createContext(null)

export function ParcoursImagesProvider({ children }) {
  const [overrides, setOverrides] = useState({})
  const [horizonsOverrides, setHorizonsOverrides] = useState({})
  const [isLoading, setIsLoading] = useState(true)

  const refresh = useCallback(async () => {
    setIsLoading(true)
    try {
      const [parcoursData, horizonsData] = await Promise.all([
        getParcoursImageOverrides(),
        getHorizonsImageOverrides(),
      ])
      setOverrides(parcoursData)
      setHorizonsOverrides(horizonsData)
    } catch (err) {
      console.warn('ParcoursImagesContext refresh:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const imageEntries = mergeImageEntries(PARCOURS_IMAGE_URLS, overrides)
  const horizonsImageEntries = mergeImageEntries(HORIZONS_IMAGE_URLS, horizonsOverrides)

  useEffect(() => {
    // #region agent log
    const emptyOverrideKeys = Object.keys(overrides).filter((k) => !getImageUrlAndCrop(overrides[k]).url)
    const naiveMerge = { ...PARCOURS_IMAGE_URLS, ...overrides }
    const sampleNodes = ['1.1', 'cadence-demi-et-parfaite', '1.2']
    const sample = sampleNodes.map((id) => {
      const raw = imageEntries[id] ?? imageEntries['1.1']
      const { url } = getImageUrlAndCrop(raw)
      return { id, urlLen: (url || '').length, fromOverride: Object.prototype.hasOwnProperty.call(overrides, id) }
    })
    const sampleNaive = sampleNodes.map((id) => {
      const raw = naiveMerge[id] ?? naiveMerge['1.1']
      const { url } = getImageUrlAndCrop(raw)
      return { id, urlLen: (url || '').length }
    })
    const default11 = getImageUrlAndCrop(PARCOURS_IMAGE_URLS['1.1']).url || ''
    fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'e7d046' },
      body: JSON.stringify({
        sessionId: 'e7d046',
        location: 'ParcoursImagesContext.jsx:merge',
        message: 'parcours imageEntries merge state',
        data: {
          overrideKeyCount: Object.keys(overrides).length,
          emptyOverrideKeys,
          defaultParcoursKeys: Object.keys(PARCOURS_IMAGE_URLS).length,
          default11UrlLen: default11.length,
          isLoading,
          sample,
          sampleNaive,
          mergeSkipsEmptyOverrides: true,
        },
        timestamp: Date.now(),
        hypothesisId: 'A',
        runId: 'post-fix',
      }),
    }).catch(() => {})
    // #endregion
  }, [overrides, isLoading])

  const saveEntry = useCallback(async (nodeId, entry) => {
    await setParcoursImageEntry(nodeId, entry)
    await refresh()
  }, [refresh])

  const saveHorizonsEntry = useCallback(async (styleId, entry) => {
    await setHorizonsImageEntry(styleId, entry)
    await refresh()
  }, [refresh])

  const value = {
    imageEntries,
    horizonsImageEntries,
    isLoading,
    saveEntry,
    saveHorizonsEntry,
    refresh,
  }

  return (
    <ParcoursImagesContext.Provider value={value}>
      {children}
    </ParcoursImagesContext.Provider>
  )
}

export function useParcoursImages() {
  const ctx = useContext(ParcoursImagesContext)
  if (!ctx) {
    throw new Error('useParcoursImages must be used within ParcoursImagesProvider')
  }
  return ctx
}
